import { ArrowUpRight, GraduationCap, Briefcase, Users, Building2, Plane, FileCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useContent } from "@/context/ContentContext";
import { loc, locArr } from "@/lib/localize";

const ICONS = {
  "graduation-cap": GraduationCap,
  "briefcase": Briefcase,
  "users": Users,
  "building-2": Building2,
  "plane": Plane,
  "file-check": FileCheck,
};

export default function ServicesSection({ limit }) {
  const { t, lang } = useLang();
  const { content } = useContent();
  const services = (content?.services || []).slice(0, limit || undefined);

  return (
    <section className="section-pad bg-[var(--bg)] relative" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <p className="overline mb-3">{t("services.overline")}</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
              {t("services.title")}
            </h2>
          </div>
          <p className="text-white/65 lg:max-w-md text-base leading-relaxed">{t("services.subtitle")}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => {
            const Icon = ICONS[s.icon] || Briefcase;
            const title = loc(s.title, lang);
            const short = loc(s.short, lang);
            const highlights = locArr(s.highlights, lang);
            return (
              <article
                key={s.id}
                className="card-dark group overflow-hidden flex flex-col fade-up"
                style={{ animationDelay: `${i * 60}ms` }}
                data-testid={`service-card-${s.id}`}
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={s.image}
                    alt={title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[var(--surface)] via-transparent to-transparent" />
                  <div className="absolute top-4 left-4 w-11 h-11 grid place-items-center rounded-full bg-gradient-to-br from-[#ffb870] to-[#c45e00] text-black shadow-[0_6px_20px_-6px_rgba(244,133,30,0.7)]">
                    <Icon size={20} strokeWidth={2.2} />
                  </div>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="font-display text-2xl font-bold text-white mb-2 group-hover:text-[var(--gold)] transition-colors">
                    {title}
                  </h3>
                  <p className="text-white/60 text-sm leading-relaxed mb-5 flex-1">{short}</p>
                  <div className="flex flex-wrap gap-2 mb-5">
                    {highlights.slice(0, 3).map((h) => (
                      <span key={h} className="text-xs px-2.5 py-1 rounded-full border border-[var(--gold)]/25 text-[var(--gold)]/90">
                        {h}
                      </span>
                    ))}
                  </div>
                  <Link
                    to={`/services?service=${s.id}`}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--gold)] hover:gap-2.5 transition-all"
                    data-testid={`service-learn-${s.id}`}
                  >
                    {t("services.learnMore")} <ArrowUpRight size={15} />
                  </Link>
                </div>
              </article>
            );
          })}
        </div>

        {limit && (
          <div className="mt-12 text-center">
            <Link to="/services" className="btn-outline-gold inline-flex" data-testid="view-all-services">
              {t("services.viewAll")} <ArrowUpRight size={16} />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
